import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

export interface MemberSlotPayload {
  slotIndex?: number;
  isComplete?: boolean;
  /** Only present for completed slots (personal data is stripped for GDPR) */
  firstName?: string;
  surname?: string;
  /** ISO date string e.g. "1990-05-15" */
  dateOfBirth?: string;
  phone?: string;
  address?: string;
  country?: string;
  purposeOfStay?: string;
  documentType?: string;
  documentNumber?: string;
  documentCountry?: string;
  visaNumber?: string;
  /** EU/CR residence permit number — only for documentType 'green_passport_eu_residence' */
  residencePermitNumber?: string;
  signatureSvg?: string;
}

export interface SubmitMemberSlotRequest {
  firstName: string;
  surname: string;
  /** ISO date string e.g. "1990-05-15" */
  dateOfBirth?: string;
  phone?: string;
  address?: string;
  country: string;
  purposeOfStay: string;
  documentType: string;
  documentNumber: string;
  documentCountry: string;
  visaNumber?: string;
  /** EU/CR residence permit number — only for documentType 'green_passport_eu_residence' */
  residencePermitNumber?: string;
  placeOfBirth?: string;
  signatureSvg: string;
  /** The guest saw the straight-line warning and confirmed; the server still recomputes the verdict. */
  signatureSimpleConfirmed?: boolean;
  hasDisabilityCard?: boolean;
}

export interface UbyportMappedOption {
  submitValue: string;
  displayCZ?: string;
  displayEN?: string;
  /** 1 = visa required, 0 = not required, -1 = not applicable */
  needVisa?: number | null;
}

export interface PublicReservationOption {
  reservationId: number;
  apartmentName: string;
  checkInDate: string;
  checkOutDate: string;
  numberOfGuests: number;
  locked: boolean;
  lockedByMaskedEmail?: string;
}

export interface PublicApartmentCheckin {
  apartmentName: string;
  apartmentAddress: string;
  windowStart: string;
  windowEnd: string;
  reservations: PublicReservationOption[];
  apartmentCapacity?: number;
  complianceNoticeCZ?: string;
  complianceNoticeEN?: string;
  guestFooterContactType?: string;
  managerFullName?: string;
  managerEmail?: string;
  managerPhone?: string;
  hostFullName?: string;
  hostPhone?: string;
  contactFullName?: string;
  contactEmail?: string;
  contactPhone?: string;
}

export interface StartPublicCheckinRequest {
  checkInDate: string;
  numberOfGuests: number;
  email: string;
  estimatedArrivalTime?: string;
}

export interface FindGuideResult {
  found: boolean;
  formToken?: string;
}

export interface StartPublicCheckinResponse {
  success: boolean;
  message: string;
  formToken?: string;
  formUrl?: string;
  lockedByMaskedEmail?: string;
}

export interface PublicCheckinForm {
  formToken: string;
  formUrl: string;
  publicCheckinKey: string;
  reservationId: number;
  apartmentName: string;
  checkInDate: string;
  checkOutDate: string;
  numberOfGuests: number;
  email: string;
  estimatedArrivalTime: string;
  states: UbyportMappedOption[];
  purposes: UbyportMappedOption[];
  guestFullName?: string;
  guestPhone?: string;
  guestAddress?: string;
  guestCity?: string;
  guestCountry?: string;
  purposeOfStay?: string;
  documentType?: string;
  documentNumber?: string;
  documentCountry?: string;
  notes?: string;
  members?: MemberSlotPayload[];
  contactFullName?: string;
  contactEmail?: string;
  contactPhone?: string;
  managerFullName?: string;
  managerEmail?: string;
  managerPhone?: string;
  hostFullName?: string;
  hostEmail?: string;
  hostPhone?: string;
  guestFooterContactType?: string;
  guideEnabled?: boolean;
  /** Manager override for guide access: "AUTO", "OPEN", or "BLOCKED" */
  guideAccessMode?: GuideAccessMode;
  /** Whether the guest must pay city tax before the guide unlocks. */
  cityTaxCollectionEnabled?: boolean;
  /** Manager waived tax for this specific reservation — the tax step is skipped entirely for the
   * guest, independent of cityTaxCollectionEnabled (which stays the apartment-level setting so the
   * manager's own preview still sees the override controls). */
  cityTaxExempt?: boolean;
  /** Whether the apartment lets guests scan their document to prefill the form (client-side MRZ OCR). */
  documentScanEnabled?: boolean;
}

export type GuideAccessMode = 'AUTO' | 'OPEN' | 'BLOCKED';

export const getPublicApartmentCheckin = async (publicKey: string) => {
  const res = await axios.get(`${API_BASE}/public/checkin/${publicKey}`);
  return res.data as PublicApartmentCheckin;
};

export const startPublicCheckin = async (publicKey: string, payload: StartPublicCheckinRequest) => {
  const res = await axios.post(`${API_BASE}/public/checkin/${publicKey}/start`, payload);
  return res.data as StartPublicCheckinResponse;
};

export const findGuideByIdentifier = async (publicKey: string, identifier: string) => {
  const res = await axios.post(`${API_BASE}/public/checkin/${publicKey}/find-guide`, { identifier });
  return res.data as FindGuideResult;
};

export const getPublicCheckinForm = async (formToken: string) => {
  const res = await axios.get(`${API_BASE}/public/checkin/form/${formToken}`);
  return res.data as PublicCheckinForm;
};

export const setFormEmail = async (formToken: string, email: string): Promise<void> => {
  await axios.put(`${API_BASE}/public/checkin/form/${formToken}/email`, { email });
};

export const submitMemberSlot = async (formToken: string, slotIndex: number, payload: SubmitMemberSlotRequest) => {
  const res = await axios.put(`${API_BASE}/public/checkin/form/${formToken}/slot/${slotIndex}`, payload);
  return res.data as MemberSlotPayload;
};
